import { defineStore } from 'pinia';
import {
  computed,
  ref,
} from 'vue';

import { accountApiConfig } from '@/config/api/account';
import { fireApiConfig } from '@/config/api/fire';
import { propertiesApiConfig } from '@/config/api/properties';
import { useClientApi } from '@/plugins/api/lk';
import { useUserRole } from '@/shared/composables';
import {
  decorateApiActions,
  prepareNextPageUrl,
} from '@/shared/lib';
import type { Deal } from '@/types/Deal';
import type { PageInfo } from '@/types/PageInfo';

import type {
  DealsFacets,
  DealsFacetsResponseData,
  DealsLookup,
  DealsNextPageArgs,
  DealsOrdering,
  DealsPropertySpecs,
  DealsRequestParams,
  DealsResponseData,
  DealsSpecs,
  DealsSpecsResponseData,
  FiredAgentDealsRequestParams,
} from './types';

export type DealsList = Array<Deal>;
export type PagedDeals = Array<DealsList>;

export interface GetDealsArgs {
  params: DealsRequestParams;
  append?: boolean;
}
export interface GetFiredAgentDealsArgs {
  params: FiredAgentDealsRequestParams;
  append?: boolean;
}

export const useDealsStore = defineStore('deals', () => {
  const pagedDeals = ref<PagedDeals>([]);
  const deals = computed(() => pagedDeals.value.flat(1));
  const count = ref(0);
  const pageInfo = ref<null | PageInfo>(null);
  const specs = ref<null | DealsSpecs>(null);
  const ordering = ref<null | DealsOrdering>(null);
  const lookup = ref<null | DealsLookup>(null);
  const facets = ref<null | DealsFacets>(null);
  const propertySpecs = ref<null | DealsPropertySpecs>(null);

  function getDealsFilterParams (params: object) {
    // TODO пока сделано так.
    //  Отфильтровываем пустые и NULL значения,
    //  т.к. для бэка это валидное значение и он по ним фильтрует
    //  Надо разобраться почему в search подставляется null.
    //  Из-за этого бэк не правильно фильтрует сделки
    return Object.fromEntries(
      Object.entries(params).filter(([key, value]) => {
        return value !== '' && value !== null;
      }),
    );
  }

  function processDealsResponse (response: DealsResponseData, append: boolean) {
    if (!append) {
      pagedDeals.value.splice(0);
    }

    if (response.result.length) {
      pagedDeals.value.push(response.result);
    }

    count.value = response.count;
    pageInfo.value = response.page_info;

    return response;
  }

  async function getDeals (args?: GetDealsArgs): Promise<DealsResponseData> {
    const { dependentRole } = accountApiConfig;
    const url = dependentRole.bookings('users', useUserRole().role);
    const params = args?.params ?? {};
    const append = args?.append ?? false;

    const response = await useClientApi().$get<DealsResponseData>(url, { params: getDealsFilterParams(params) });

    return processDealsResponse(response, append);
  }

  async function getFiredAgentDeals (args?: GetFiredAgentDealsArgs): Promise<DealsResponseData> {
    const url = fireApiConfig.represesAgentsDealsToAccept;
    const params = args?.params ?? {};
    const append = args?.append ?? false;

    const response = await useClientApi().$get<DealsResponseData>(url, { params: getDealsFilterParams(params) });

    return processDealsResponse(response, append);
  }

  async function getSpecs (): Promise<DealsSpecsResponseData> {
    const { bookings } = accountApiConfig;
    const url = bookings.specsV2(useUserRole().role);

    const response = await useClientApi().$get<DealsSpecsResponseData>(url);
    specs.value = response.specs;
    ordering.value = response.ordering;

    return response;
  }

  async function getPropertySpecs (): Promise<DealsPropertySpecs> {
    const url = propertiesApiConfig.typeSpecs;
    const result = await useClientApi().$get<DealsPropertySpecs>(url);

    propertySpecs.value = [
      {
        label: 'Все',
        value: undefined,
      },
      ...result,
    ];

    return propertySpecs.value;
  }

  async function getFacets (params: DealsRequestParams): Promise<void> {
    const { bookings } = accountApiConfig;
    const url = bookings.facets(useUserRole().role);
    const response = await useClientApi().$get<DealsFacetsResponseData>(url, { params });
    facets.value = response.facets;
  }

  async function fetchLookup (params: DealsRequestParams): Promise<DealsLookup> {
    const { bookings } = accountApiConfig;
    const url = bookings.lookup(useUserRole().role);
    lookup.value = await useClientApi().$get<DealsLookup>(url, { params });

    return lookup.value;
  }

  async function nextPage ({ page, infiniteState }: DealsNextPageArgs): Promise<void> {
    try {
      if (!page?.next_page) {
        infiniteState.complete();

        return;
      }
      const response = await useClientApi().$get<DealsResponseData>(prepareNextPageUrl(page.next_page));

      if (!response.page_info) {
        throw new Error('page info not received');
      }

      if (response.result?.length) {
        pageInfo.value = response.page_info;
        pagedDeals.value.push(response.result);

        if (!pageInfo.value.next_page) {
          infiniteState.complete();
        } else {
          infiniteState.loaded();
        }
      } else {
        infiniteState.loaded();
      }
    } catch (error) {
      infiniteState.complete();
      throw error;
    }
  }

  function dropLookupItems (): void {
    lookup.value = null;
  }

  function clear () {
    pagedDeals.value = [];
  }

  const decoratedApiActions = decorateApiActions({
    getDeals,
    getFiredAgentDeals,
    getSpecs,
    getPropertySpecs,
    getFacets,
    fetchLookup,
    nextPage,
  }, 'dealsPinia');

  const decoratedApiActionsRethrow = decorateApiActions({
    getDealsRethrow: getDeals,
    getFiredAgentDealsRethrow: getFiredAgentDeals,
  }, 'dealsPinia', true);

  return {
    deals,
    pagedDeals,
    count,
    pageInfo,
    specs,
    ordering,
    lookup,
    facets,
    propertySpecs,
    dropLookupItems,
    clear,
    ...decoratedApiActions,
    ...decoratedApiActionsRethrow,
  };
});
